/* eslint-disable @typescript-eslint/naming-convention */
import React, {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    HTMLAttributes,
    memo,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useMemo,
} from 'react';
import classNames from 'classnames';
import style from './style.module.scss';
import {TTypographyVariant, Typography} from 'components/Typography';
import {Loader, LOADER_COLOR, TLoaderColor} from 'components/Loader';
import {LinkProps} from 'next/link';
import {PREVENT_DEFAULT_HANDLER_MARKER, STOP_PROP_HANDLER_MARKER} from 'core/constants';
import {NEXT_PUBLIC_WINTER_DESIGN} from 'core/helpers/env';

export const BUTTON_SIZE = {
    large: 'large',
    medium: 'medium',
    small: 'small',
    huge: 'huge',
    unset: 'unset',
    ['fixed-large']: 'fixed-large',
    ['fixed-medium']: 'fixed-medium',
    ['fixed-small']: 'fixed-small',
    ['fixed-huge']: 'fixed-huge',
} as const;

export type TButtonSize = keyof typeof BUTTON_SIZE;

export const BUTTON_THEMES = {
    black: 'black',
    gray: 'gray',
    white: 'white',
    red: 'red',
    outline: 'outline',
    ghost: 'ghost',
    unset: 'unset',
    inherit: 'inherit',
} as const;

export type TButtonTheme = keyof typeof BUTTON_THEMES;

type TButtonSizeVariant = {
    DT: 'large' | 'medium' | 'small' | 'huge' | 'fixed-large' | 'fixed-medium' | 'fixed-small' | 'fixed-huge';
    TB: 'large' | 'medium' | 'small' | 'huge' | 'fixed-large' | 'fixed-medium' | 'fixed-small' | 'fixed-huge';
    MB: 'large' | 'medium' | 'small' | 'huge' | 'fixed-large' | 'fixed-medium' | 'fixed-small' | 'fixed-huge';
};

export interface IProps
    extends
        ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
        Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel'> {
    variant?: TButtonSize;
    className?: string;
    theme?: TButtonTheme;
    isLoading?: boolean;
    disabled?: boolean;
    children?: ReactNode;

    // флаги состояния
    isSquare?: boolean;
    isStretched?: boolean;
    isWithoutPaddings?: boolean;
    isWithoutTextLoading?: boolean;
    /** Явно указывает, что кнопка содержит только SVG иконку */
    isIconOnly?: boolean;

    // иконки
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;

    // стилизация
    textClassName?: string;

    // адаптивные размеры
    variants?: TButtonSizeVariant;

    // маркеры
    [PREVENT_DEFAULT_HANDLER_MARKER]?: boolean;
    [STOP_PROP_HANDLER_MARKER]?: boolean;

    // зимнее оформление NEW_YEAR_2026
    isWinterDesign?: boolean;
}

const mapSizeToTypographyVariant = (adaptiveSize: TButtonSize): TTypographyVariant => {
    const sizeMap: Record<TButtonSize, TTypographyVariant> = {
        small: 'label-12-r',
        medium: 'label-14-r',
        large: 'body-16-r',
        huge: 'body-18-r',
        unset: 'body-18-r',
        'fixed-huge': 'body-18-r',
        'fixed-medium': 'label-14-r',
        'fixed-large': 'body-16-r',
        'fixed-small': 'label-12-r',
    } as const;

    return sizeMap[adaptiveSize] || 'body-18-r';
};

const isIconButtonCheck = (children: ReactNode): boolean => {
    if (!children) {
        return false;
    }

    const checkIfIcon = (child: React.ReactElement): boolean => {
        // если это SVG элемент
        if ('svg' === child.type) {
            return true;
        }

        // если это компонент SvgIcon
        if ('function' === typeof child.type && 'SvgIcon' === child.type.name) {
            return true;
        }

        // если это компонент с именем, содержащим "Icon" или "Svg"
        if (
            'function' === typeof child.type &&
            (child.type.name?.toLowerCase().includes('icon') || child.type.name?.toLowerCase().includes('svg'))
        ) {
            return true;
        }

        if ('object' === typeof child) {
            return Boolean(child.props.svg);
        }

        return false;
    };

    if (Array.isArray(children) && 1 === children.length) {
        const child = children[0];
        return React.isValidElement(child) && checkIfIcon(child);
    }

    return React.isValidElement(children) && checkIfIcon(children);
};

const ButtonContent = memo<{
    isLoading: boolean;
    children: ReactNode;
    leftIcon: ReactNode;
    rightIcon: ReactNode;
    typographyVariant: TTypographyVariant;
    typographyVariants?: {DT: TTypographyVariant; TB: TTypographyVariant; MB: TTypographyVariant};
    textClassName?: string;
    loaderColor?: TLoaderColor;
    isIconButton?: boolean;
}>(
    // eslint-disable-next-line complexity
    ({
        isLoading,
        children,
        leftIcon,
        rightIcon,
        typographyVariant,
        typographyVariants,
        textClassName,
        loaderColor,
        isIconButton = false,
    }) => {
        const loader = useMemo(
            () => (
                <div className={style.loader}>
                    <Loader color={loaderColor} />
                </div>
            ),
            [loaderColor],
        );

        return (
            <>
                {isLoading && leftIcon ? loader : leftIcon}

                {children && !isIconButton && (
                    <Typography
                        className={classNames(style.text, textClassName, {
                            [style.isTextHidden]: isLoading,
                        })}
                        variant={typographyVariant || typographyVariants?.DT}
                        variants={typographyVariants}
                    >
                        {children}
                    </Typography>
                )}

                {!isLoading && isIconButton && children}

                {isLoading && !leftIcon && !rightIcon && loader}
                {isLoading && rightIcon ? loader : rightIcon}
            </>
        );
    },
);

export const ButtonNew = memo<IProps>(
    ({
        variant,
        theme,
        isLoading = false,
        disabled = false,
        className,
        children,
        isSquare = false,
        isStretched = false,
        isIconOnly = false,
        leftIcon,
        rightIcon,
        type = 'button',
        onClick,
        href,
        target,
        rel,
        textClassName,
        variants,
        isWinterDesign = false,
        ...restButtonAttributes
    }) => {
        const handleClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
            (e) => {
                if (isLoading) {
                    e.preventDefault();
                } else {
                    onClick?.(e);
                }
            },
            [isLoading, onClick],
        );

        const TargetElement = useMemo<'a' | 'button'>(() => {
            return href ? 'a' : 'button';
        }, [href]);

        const targetAttributes = useMemo<HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>>(() => {
            if ('a' === TargetElement) {
                return {
                    href,
                    rel,
                    target,
                } as HTMLAttributes<HTMLAnchorElement> & LinkProps;
            }

            return {
                type,
            } as HTMLAttributes<HTMLButtonElement>;
        }, [TargetElement, href, rel, target, type]);

        const isIconButton = useMemo(
            () => Boolean((!children && (leftIcon || rightIcon)) || isIconButtonCheck(children) || isIconOnly),
            [children, leftIcon, rightIcon, isIconOnly],
        );

        const responsiveSizeClasses = useMemo(() => {
            if (variant) {
                return [style[variant]];
            }
            if (variants) {
                return [style[`dt-${variants.DT}`], style[`tb-${variants.TB}`], style[`mb-${variants.MB}`]];
            }
            return [style.huge] as (string | undefined)[];
        }, [variant, variants]);

        const loaderColor = useMemo(() => {
            const colors: Record<TButtonTheme, TLoaderColor> = {
                [BUTTON_THEMES.black]: LOADER_COLOR.white,
                [BUTTON_THEMES.gray]: LOADER_COLOR.black,
                [BUTTON_THEMES.white]: LOADER_COLOR.black,
                [BUTTON_THEMES.red]: LOADER_COLOR.white,
                [BUTTON_THEMES.outline]: LOADER_COLOR.black,
                [BUTTON_THEMES.ghost]: LOADER_COLOR.red,
                [BUTTON_THEMES.inherit]: LOADER_COLOR.black,
                unset: 'white',
            };
            // @ts-expect-error
            return colors[theme] || 'white';
        }, [theme]);

        const typographyVariantProps = useMemo<
            | {variant: TTypographyVariant; variants?: undefined}
            | {variant?: undefined; variants: {DT: TTypographyVariant; TB: TTypographyVariant; MB: TTypographyVariant}}
        >(() => {
            if (variant) {
                return {variant: mapSizeToTypographyVariant(variant)};
            }

            if (variants) {
                return {
                    variants: {
                        DT: mapSizeToTypographyVariant(variants.DT),
                        TB: mapSizeToTypographyVariant(variants.TB),
                        MB: mapSizeToTypographyVariant(variants.MB),
                    },
                };
            }

            return {variant: mapSizeToTypographyVariant('unset')};
        }, [variant, variants]);

        const buttonClasses = useMemo(
            () =>
                classNames('button-new', 'simple-button', className, style.button, ...responsiveSizeClasses, {
                    [style.isLoading]: isLoading,
                    [style.isSquare]: isSquare || isIconButton,
                    [style.isStretched]: isStretched,
                    [style.isLeftIcon]: Boolean(leftIcon),
                    [style.isRightIcon]: Boolean(rightIcon),
                    [style[theme as string]]: Boolean(theme),
                    // NEW_YEAR_2026
                    'winter-design': isWinterDesign && NEXT_PUBLIC_WINTER_DESIGN,
                }),
            [
                className,
                responsiveSizeClasses,
                isLoading,
                isSquare,
                isIconButton,
                isStretched,
                leftIcon,
                rightIcon,
                theme,
                isWinterDesign,
            ],
        );

        return (
            <TargetElement
                {...targetAttributes}
                {...restButtonAttributes}
                className={buttonClasses}
                disabled={disabled}
                onClick={handleClick}
            >
                <ButtonContent
                    isLoading={isLoading}
                    children={children}
                    leftIcon={leftIcon}
                    rightIcon={rightIcon}
                    typographyVariant={typographyVariantProps.variant || typographyVariantProps.variants?.DT}
                    typographyVariants={typographyVariantProps.variants}
                    textClassName={textClassName}
                    loaderColor={loaderColor}
                    isIconButton={isIconButton}
                />
            </TargetElement>
        );
    },
);
